import {
  useAuthControllerGetCurrentUser,
  useAuthControllerUpdateUser,
} from "@/lib/lutely-api/gen/auth/auth";

import type {} from "@/lib/lutely-api/gen/auth/auth";
import { UseQueryResult } from "@tanstack/react-query";
import { useContext, useEffect, useRef } from "react";
import type { ApiContextType } from "./ApiContext";
import ApiContext from "./ApiContext";
import {
  BaseCreatorDto,
  SubscriptionDto,
  UserPayloadDto,
  UserPayloadDtoLabelRole,
} from "@/lib/lutely-api/gen/dtos";
import { ErrorType } from "@/lib/lutely-api/api-request";
import { useAuthControllerOauthInit } from "@/lib/lutely-api/gen/auth/auth";
import { analytics, EventEnum } from "@/lib/analytics";

// useLoggedInUser is mounted by many components at once. Track the userId we've
// fired timezone capture for (module scope, so it's shared across all mounted
// consumers). This dedupes concurrent attempts but still allows a retry if the
// update fails, and re-runs when a different user logs in.
let timezoneCaptureUserId: string | null = null;

export const useApi = (): ApiContextType => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error("Must be used within an ApiProvider");
  }
  return context;
};

export const useIsApiConfigured = (): boolean => {
  const context = useApi();
  return context.isAuthenticated && context.apiClientConfigured;
};

export const useLoggedInUser = (): UseQueryResult<
  UserPayloadDto,
  ErrorType<unknown>
> & {
  defaultCreator?: BaseCreatorDto;
  creators: BaseCreatorDto[];
  subscription?: SubscriptionDto;
  hasActiveSubscription: boolean;
  isCreatorSubscriber: boolean;
  isLabelSubscriber: boolean;
  labelRole?: UserPayloadDtoLabelRole;
  isLabelOwner: boolean;
  canSwitchArtists: boolean;
  isAdmin: boolean;
} => {
  const { logoutUser, isPaused, getTokens, isAdmin } = useApi();
  const hasRetriedRef = useRef(false);
  const result = useAuthControllerGetCurrentUser({
    query: {
      enabled: useIsApiConfigured() && !isPaused,
    },
  });

  useEffect(() => {
    if (!result.isError || result.isFetching) return;

    const status = result.error?.response?.status;

    if (status !== 401 && status !== 403) return;

    if (hasRetriedRef.current) {
      logoutUser();
      return;
    }

    hasRetriedRef.current = true;

    (async () => {
      try {
        // Force refresh tokens
        const tokens = await getTokens(true);

        // If refresh failed, logoutUser() already ran
        if (!tokens?.access) {
          return;
        }

        // Retry auth/me once
        await result.refetch();
      } catch {
        logoutUser();
      }
    })();
  }, [logoutUser, getTokens, result]);

  const { data: user } = result;

  // One-time capture of the user's browser timezone so transactional emails can
  // localize timestamps. Fires only when no timezone is stored yet, at most once
  // per user across all mounted consumers, and retries later if it fails.
  const { mutate: updateUser } = useAuthControllerUpdateUser();
  useEffect(() => {
    if (!user || user.timezone) return;
    if (timezoneCaptureUserId === user.userId) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    timezoneCaptureUserId = user.userId;
    updateUser(
      { data: { timezone: tz } },
      {
        onError: () => {
          // Release the guard so it can retry later in the session.
          if (timezoneCaptureUserId === user.userId) {
            timezoneCaptureUserId = null;
          }
        },
      },
    );
  }, [user, updateUser]);

  const creators: BaseCreatorDto[] = user?.creators ?? [];
  let defaultCreator: BaseCreatorDto | undefined = undefined;
  if (creators.length) {
    defaultCreator = creators.find((c) => c.isDefault) ?? creators[0];
  }

  let subscription: SubscriptionDto | undefined = undefined;
  if (user?.subscriptions?.length) {
    // TODO: Does this need to handle multiple subscriptions?
    subscription =
      user.subscriptions.find((sub) => sub.isActive) || user.subscriptions[0];
  }

  const hasActiveSubscription = subscription?.isActive ?? false;

  const isCreatorSubscriber =
    hasActiveSubscription && subscription?.subscriptionTier === "creator";

  // The label Stripe product is marketed as "Pro": the API moved the wire
  // value from "label" to "pro" — accept both.
  const isLabelSubscriber =
    hasActiveSubscription &&
    ((subscription?.subscriptionTier as string) === "label" ||
      subscription?.subscriptionTier === "pro");

  // Labels switch artists by plan; accounts that still own several artists
  // (e.g. after a downgrade) keep switching too — their non-default artists
  // are read-only, but must stay reachable.
  const canSwitchArtists = isLabelSubscriber || creators.length > 1;

  const labelRole = user?.labelRole;
  const isLabelOwner = labelRole === UserPayloadDtoLabelRole.OWNER;

  return {
    ...result,
    defaultCreator,
    creators,
    subscription,
    hasActiveSubscription,
    isCreatorSubscriber,
    isLabelSubscriber,
    labelRole,
    isLabelOwner,
    canSwitchArtists,
    isAdmin,
  };
};

export const useOauthInitUser = () => {
  const api = useApi();
  const providerRef = useRef<"google" | "apple" | "unknown">("unknown");

  const oauthInitMutation = useAuthControllerOauthInit({
    mutation: {
      onSuccess: async () => {
        analytics.track(EventEnum.USER_LOGGED_IN, {
          method: "oauth",
          provider: providerRef.current,
        });
      },
      onSettled: async () => {
        // Guarantees we never leave the API paused
        if (api.isPaused) {
          api.pauseApi(false);
        }
      },
    },
  });

  /**
   * Pause the API as soon as this hook is mounted.
   * Prevents auth/me or other queries from racing OAuth init.
   */
  useEffect(() => {
    if (!api.isPaused) {
      api.pauseApi(true);
    }

    // Cleanup is defensive only, onSettled should handle this
    return () => {
      if (api.isPaused) {
        api.pauseApi(false);
      }
    };
  }, [api]);

  return {
    ...oauthInitMutation,

    // Wrap mutateAsync so we can attach provider
    mutateAsyncWithProvider: async (
      provider: "google" | "apple" | "unknown",
      variables: Parameters<typeof oauthInitMutation.mutateAsync>[0],
    ) => {
      providerRef.current = provider;
      return oauthInitMutation.mutateAsync(variables);
    },
  };
};

export const useLogin = () => useApi().loginUser;
export const useOtcLogin = () => useApi().otcLoginUser;
export const useSignUp = () => useApi().signUpUser;
export const useLogout = () => useApi().logoutUser;
export const useGetRedirectPath = () => useApi().getRedirectPath;
